/**
 * Shared ledger view for both customers and suppliers - table, filters,
 * summary cards, export, and (customer-only) a "Record Payment" row action
 * on outstanding invoices. Used by PartyLedgersPage.tsx directly, and reached
 * via a deep link with ?customerId=/?supplierId= for a specific party.
 *
 * Two bugs this replaces (from the old CustomerLedgerPage.tsx):
 * - Flicker: two separate loading states (summary vs entries) instead of one
 *   shared `loading` flag toggled by two independent, uncoordinated effects.
 * - Broken filters: date/type controls write straight into `filters`, the
 *   same object the entries-fetch effect depends on.
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Skeleton,
  Tooltip,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  TableChart as CsvIcon,
  AccountBalance as BalanceIcon,
  TrendingUp as DebitIcon,
  TrendingDown as CreditIcon,
  FilterList as FilterIcon,
  Visibility as PreviewIcon,
  Payment as PaymentActionIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';

import {
  LedgerService,
  LedgerResponse,
  LedgerSummary,
  LedgerFilters,
  LedgerEntry,
  PartyType,
  LEDGER_TRANSACTION_TYPES,
} from '../../services/ledgerService';
import { documentInvoiceService } from '../../services/documentInvoiceService';
import { Invoice } from '../../types/payment';
import PaymentRecordDialog from '../documents/PaymentRecordDialog';

interface LedgerViewProps {
  partyType: PartyType;
  partyId: string;
  partyName: string;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount);

const getBalanceColor = (balance: number) => {
  if (balance > 0) return 'error.main';
  if (balance < 0) return 'success.main';
  return 'text.primary';
};

const getBalanceIcon = (balance: number) => {
  if (balance > 0) return <DebitIcon color="error" />;
  if (balance < 0) return <CreditIcon color="success" />;
  return <BalanceIcon />;
};

const getTransactionTypeChip = (type: string) => {
  const typeConfig: Record<string, { label: string; color: 'error' | 'success' | 'info' | 'warning' | 'default' | 'secondary' }> = {
    invoice: { label: 'Invoice', color: 'error' },
    payment: { label: 'Payment', color: 'success' },
    credit_note: { label: 'Credit Note', color: 'info' },
    debit_note: { label: 'Debit Note', color: 'warning' },
    opening_balance: { label: 'Opening Balance', color: 'default' },
    adjustment: { label: 'Adjustment', color: 'secondary' },
  };
  const config = typeConfig[type] || { label: type, color: 'default' as const };
  return <Chip label={config.label} color={config.color} size="small" />;
};

const LedgerView: React.FC<LedgerViewProps> = ({ partyType, partyId, partyName }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [summary, setSummary] = useState<LedgerSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [ledgerData, setLedgerData] = useState<LedgerResponse | null>(null);
  const [entriesLoading, setEntriesLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [filters, setFilters] = useState<LedgerFilters>({ page: 1, page_size: 25 });

  const [paymentDialogInvoice, setPaymentDialogInvoice] = useState<Invoice | null>(null);
  const [paymentDialogLoading, setPaymentDialogLoading] = useState(false);

  const handleFilterChange = (newFilters: Partial<LedgerFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  // Summary loads once per party - independent of entries/filters, so it
  // doesn't bounce the whole page in and out of a loading state.
  useEffect(() => {
    let cancelled = false;
    setSummaryLoading(true);
    LedgerService.getSummary(partyType, partyId)
      .then((data) => { if (!cancelled) setSummary(data); })
      .catch((err) => {
        console.error('Error loading ledger summary:', err);
        if (!cancelled) enqueueSnackbar('Failed to load ledger summary', { variant: 'error' });
      })
      .finally(() => { if (!cancelled) setSummaryLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyType, partyId]);

  // Entries reload whenever the party or any filter (including date range /
  // transaction type, now correctly wired) changes.
  useEffect(() => {
    let cancelled = false;
    setEntriesLoading(true);
    LedgerService.getLedger(partyType, partyId, filters)
      .then((data) => { if (!cancelled) setLedgerData(data); })
      .catch((err) => {
        console.error('Error loading ledger entries:', err);
        if (!cancelled) enqueueSnackbar('Failed to load ledger entries', { variant: 'error' });
      })
      .finally(() => { if (!cancelled) setEntriesLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyType, partyId, filters]);

  const handleClearFilters = () => {
    setFilters({ page: 1, page_size: filters.page_size });
  };

  const handlePreviewPdf = async () => {
    if (partyType !== 'customer') return;
    try {
      setExporting(true);
      const blob = await LedgerService.downloadLedgerPdf(partyId, {
        start_date: filters.start_date,
        end_date: filters.end_date,
        transaction_type: filters.transaction_type,
        include_summary: true,
      });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err: any) {
      console.error('Error previewing PDF:', err);
      enqueueSnackbar('Failed to preview PDF', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (partyType !== 'customer') return;
    try {
      setExporting(true);
      const blob = await LedgerService.downloadLedgerPdf(partyId, {
        start_date: filters.start_date,
        end_date: filters.end_date,
        transaction_type: filters.transaction_type,
        include_summary: true,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ledger_${partyName.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('Ledger PDF downloaded successfully', { variant: 'success' });
    } catch (err: any) {
      console.error('Error exporting PDF:', err);
      enqueueSnackbar('Failed to export PDF', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const allEntries = await LedgerService.getLedger(partyType, partyId, {
        start_date: filters.start_date,
        end_date: filters.end_date,
        transaction_type: filters.transaction_type,
        page: 1,
        page_size: 10000,
      });

      const csvHeaders = ['Date', 'Type', 'Reference', 'Description', 'Debit Amount', 'Credit Amount', 'Balance'];
      const csvRows = allEntries.entries.map((entry) => [
        format(parseISO(entry.transaction_date), 'dd/MM/yyyy'),
        entry.transaction_type.replace('_', ' ').toUpperCase(),
        entry.reference_number,
        entry.description || '',
        entry.debit_amount > 0 ? entry.debit_amount.toFixed(2) : '',
        entry.credit_amount > 0 ? entry.credit_amount.toFixed(2) : '',
        entry.balance.toFixed(2),
      ]);

      if (allEntries.balance_summary) {
        csvRows.unshift([]);
        csvRows.unshift(['SUMMARY', '', '', '', '', '', '']);
        csvRows.unshift(['Opening Balance', '', '', '', '', '', allEntries.balance_summary.opening_balance.toFixed(2)]);
        csvRows.unshift(['Total Debits', '', '', '', allEntries.balance_summary.total_debits.toFixed(2), '', '']);
        csvRows.unshift(['Total Credits', '', '', '', '', allEntries.balance_summary.total_credits.toFixed(2), '']);
        csvRows.unshift(['Closing Balance', '', '', '', '', '', allEntries.balance_summary.closing_balance.toFixed(2)]);
        csvRows.unshift([]);
      }

      const csvContent = [csvHeaders, ...csvRows].map((row) => row.map((f) => `"${f}"`).join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ledger_${partyName.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('Ledger CSV downloaded successfully', { variant: 'success' });
    } catch (err: any) {
      console.error('Error exporting CSV:', err);
      enqueueSnackbar('Failed to export CSV', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleOpenRecordPayment = async (entry: LedgerEntry) => {
    setPaymentDialogLoading(true);
    try {
      const invoice = await documentInvoiceService.getInvoice(entry.reference_id);
      setPaymentDialogInvoice(invoice);
    } catch (err: any) {
      console.error('Error loading invoice for payment:', err);
      enqueueSnackbar('Failed to load invoice details', { variant: 'error' });
    } finally {
      setPaymentDialogLoading(false);
    }
  };

  const handleSubmitPayment = async (paymentData: any) => {
    if (!paymentDialogInvoice) return;
    const response = await fetch('/api/admin/payments', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({
        invoice_id: paymentDialogInvoice.id,
        payment_date: paymentData.payment_date
          ? (typeof paymentData.payment_date === 'string' ? paymentData.payment_date : paymentData.payment_date.toISOString().split('T')[0])
          : new Date().toISOString().split('T')[0],
        payment_method: paymentData.payment_method.toLowerCase(),
        amount: paymentData.amount,
        reference_number: paymentData.transaction_id || paymentData.reference_number,
        notes: paymentData.notes,
      }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to record payment');
    }
    enqueueSnackbar('Payment recorded successfully', { variant: 'success' });
    setPaymentDialogInvoice(null);
    // Refresh both entries and summary so the new payment and updated balance show up.
    setFilters((prev) => ({ ...prev }));
    LedgerService.getSummary(partyType, partyId).then(setSummary).catch((err: any) => console.error(err));
  };

  const columns: GridColDef[] = [
    {
      field: 'transaction_date',
      headerName: 'Date',
      width: 120,
      renderCell: (params) => format(parseISO(params.value), 'dd/MM/yyyy'),
    },
    {
      field: 'transaction_type',
      headerName: 'Type',
      width: 140,
      renderCell: (params) => getTransactionTypeChip(params.value),
    },
    {
      field: 'reference_number',
      headerName: 'Reference',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">{params.value}</Typography>
      ),
    },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
    {
      field: 'debit_amount',
      headerName: 'Debit',
      width: 120,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2" color={params.value > 0 ? 'error.main' : 'text.disabled'}>
          {params.value > 0 ? formatCurrency(params.value) : '-'}
        </Typography>
      ),
    },
    {
      field: 'credit_amount',
      headerName: 'Credit',
      width: 120,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2" color={params.value > 0 ? 'success.main' : 'text.disabled'}>
          {params.value > 0 ? formatCurrency(params.value) : '-'}
        </Typography>
      ),
    },
    {
      field: 'balance',
      headerName: 'Balance',
      width: 140,
      align: 'right',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {getBalanceIcon(params.value)}
          <Typography variant="body2" color={getBalanceColor(params.value)} fontWeight="medium">
            {formatCurrency(Math.abs(params.value))}
          </Typography>
        </Box>
      ),
    },
    ...(partyType === 'customer' ? [{
      field: 'actions',
      headerName: 'Actions',
      width: 90,
      sortable: false,
      renderCell: (params: any) => {
        const entry = params.row as LedgerEntry;
        if (entry.transaction_type !== 'invoice' || entry.balance <= 0) return null;
        return (
          <Tooltip title="Record Payment">
            <span>
              <IconButton size="small" color="primary" disabled={paymentDialogLoading}
                onClick={() => handleOpenRecordPayment(entry)}>
                <PaymentActionIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    } as GridColDef] : []),
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Typography variant="h6" fontWeight={700}>{partyName}</Typography>
          <Stack direction="row" spacing={1}>
            {partyType === 'customer' && (
              <>
                <Button size="small" variant="outlined" startIcon={<PreviewIcon />} onClick={handlePreviewPdf} disabled={exporting}>
                  Preview PDF
                </Button>
                <Button size="small" variant="contained" color="secondary" startIcon={<PdfIcon />} onClick={handleDownloadPdf} disabled={exporting}>
                  Download PDF
                </Button>
              </>
            )}
            <Button size="small" variant="outlined" startIcon={<CsvIcon />} onClick={handleExportCsv} disabled={exporting}>
              Export CSV
            </Button>
          </Stack>
        </Box>

        {/* Balance Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Current Balance</Typography>
                {summaryLoading ? <Skeleton width={120} height={36} /> : summary ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getBalanceIcon(summary.current_balance)}
                      <Typography variant="h5" color={getBalanceColor(summary.current_balance)}>
                        {formatCurrency(Math.abs(summary.current_balance))}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {summary.current_balance > 0 ? `${partyType === 'customer' ? 'Customer' : 'We'} Owe${summary.current_balance > 0 && partyType === 'customer' ? 's' : ''}`
                        : summary.current_balance < 0 ? 'Balanced in our favor' : 'Balanced'}
                    </Typography>
                  </>
                ) : null}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Total Transactions</Typography>
                {summaryLoading ? <Skeleton width={60} height={36} /> : (
                  <Typography variant="h5">{summary?.total_transactions ?? 0}</Typography>
                )}
                <Typography variant="caption" color="text.secondary">All time</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Latest Transaction</Typography>
                {summaryLoading ? <Skeleton width={100} height={28} /> : (
                  <Typography variant="h6">
                    {summary?.latest_transaction_date ? format(parseISO(summary.latest_transaction_date), 'dd/MM/yyyy') : 'No transactions'}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Transaction Types</Typography>
                {summaryLoading ? <Skeleton height={60} /> : (
                  <Stack spacing={0.5}>
                    {Object.entries(summary?.transaction_counts || {}).map(([type, count]) => (
                      <Typography key={type} variant="caption">{type.replace('_', ' ')}: {count}</Typography>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon /> Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Start Date"
                value={filters.start_date ? parseISO(filters.start_date) : null}
                onChange={(date) => handleFilterChange({ start_date: date ? format(date, 'yyyy-MM-dd') : undefined, page: 1 })}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="End Date"
                value={filters.end_date ? parseISO(filters.end_date) : null}
                onChange={(date) => handleFilterChange({ end_date: date ? format(date, 'yyyy-MM-dd') : undefined, page: 1 })}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Transaction Type</InputLabel>
                <Select
                  value={filters.transaction_type || ''}
                  onChange={(e) => handleFilterChange({ transaction_type: e.target.value || undefined, page: 1 })}
                  input={<OutlinedInput label="Transaction Type" />}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {LEDGER_TRANSACTION_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button variant="outlined" onClick={handleClearFilters} fullWidth>Clear Filters</Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Period Summary */}
        {ledgerData?.balance_summary && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Period Summary</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">Opening Balance</Typography>
                <Typography variant="h6" color={getBalanceColor(ledgerData.balance_summary.opening_balance)}>
                  {formatCurrency(ledgerData.balance_summary.opening_balance)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">Total Debits</Typography>
                <Typography variant="h6" color="error.main">{formatCurrency(ledgerData.balance_summary.total_debits)}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">Total Credits</Typography>
                <Typography variant="h6" color="success.main">{formatCurrency(ledgerData.balance_summary.total_credits)}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">Closing Balance</Typography>
                <Typography variant="h6" color={getBalanceColor(ledgerData.balance_summary.closing_balance)}>
                  {formatCurrency(ledgerData.balance_summary.closing_balance)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Ledger Entries Table */}
        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={ledgerData?.entries || []}
            columns={columns}
            loading={entriesLoading}
            paginationMode="server"
            rowCount={ledgerData?.total_count || 0}
            paginationModel={{ page: (filters.page || 1) - 1, pageSize: filters.page_size || 25 }}
            onPaginationModelChange={(model) => handleFilterChange({ page: model.page + 1, page_size: model.pageSize })}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            sx={{ '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' } }}
          />
        </Paper>

        {paymentDialogInvoice && (
          <PaymentRecordDialog
            open={Boolean(paymentDialogInvoice)}
            invoice={paymentDialogInvoice as any}
            onClose={() => setPaymentDialogInvoice(null)}
            onSubmit={handleSubmitPayment}
          />
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default LedgerView;
